/** @jsxImportSource @emotion/react */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
// import { Link } from "react-router-dom";
import { useMemo } from "react";
import useConfig from "../../../../actions/Tenants/config/configHook";
import { MailRecipients } from "../../Helpers";
import { PageBase, PageBaseTableColumns } from "../PageBase";
import { SupplierInfo } from "../Supplier/types";
import { defineInternalEmailsFormCreate } from "./defineInternalEmailsFormCreate";
import { usePagination } from "../../../../hooks";
// import { useImportExport } from "../../../Helper/ImportExportModal/useImportExport";
import { callAvosApi } from "../../../../utils/useAvosApiHook";
import { ClientInfo } from "../Client/types";

const endpoint = "/supplier/email_recipients";

const InternalEmailsPage = () => {
    const config = useConfig();
    const queryClient = useQueryClient();

    const { offset, limit } = usePagination();

    // const importFields = [
    //     {
    //         label: "fruit_types",
    //         description: <span>Only fruit types configured in <Link to={`/tenants/${config?.tenant_id}/form-field-options/fruit_type`}>fruit type form options</Link> are valid. Multiple values separated by comma.</span>,
    //         required: true,
    //     },
    //     { label: "report_type", description: "Report types (multiple values separated by comma). Leaving it empty will send all report types." },
    //     { label: "report_status", description: "Report statuses (multiple values separated by comma). Leaving it empty will send all report statuses." },
    //     { label: "emails", description: "Emails (multiple values separated by comma)." },
    // ];

    // const entityName = "supplier/email_recipients";
    // const modalProps = useImportExport({
    //     entityName,
    //     importFields,
    // });

    const getEmails = async (offset, limit) => {
        const res = await callAvosApi(`${endpoint}?limit=${limit}&offset=${offset}`);
        return res.data;
    };

    const { data: emails, isLoading } = useQuery({
        queryKey: ["emails", offset, limit],
        queryFn: () => getEmails(offset, limit),
    });

    const invalidateQueries = () => {
        queryClient.invalidateQueries({ queryKey: ["emails"], exact: false });
        queryClient.invalidateQueries({ queryKey: ["all-emails"], exact: false });
    };

    const { mutate: saveEmail } = useMutation({
        mutationFn: async (info: SupplierInfo | ClientInfo) => {
            return callAvosApi(`${endpoint}${info.id ? `/${info.id}` : ""}`, {
                method: info.id ? "PUT" : "POST",
                data: info,
            });
        },
        onSuccess: () => {
            invalidateQueries();
        },
    });

    const { mutate: deleteEmail } = useMutation({
        mutationFn: async (id: string) => {
            return callAvosApi(`${endpoint}/${id}`, {
                method: "DELETE",
            });
        },
        onSuccess: () => {
            invalidateQueries();
        },
    });

    const onSaveInternalEmails = async (info: SupplierInfo | ClientInfo) => {
        await saveEmail(info);
    };

    const onDeleteEmail = async (id: string) => {
        await deleteEmail(id);
    };

    const emailState = useMemo(() => {
        return {
            items: emails || { results: [], count: 0 },
            isLoading,
            filter: { offset, limit },
            localFiltering: false,
            reload: false
        };
    }, [emails, isLoading, offset, limit]);

    const tableRow: PageBaseTableColumns<SupplierInfo>[] = [
        {
            label: "Fruit",
            name: "fruit_type",
            display_component(entity: SupplierInfo) {
                return config.get_form_option_label("fruit_type", entity.fruit_type) || "All";
            },
            lg: 2,
        },
        {
            label: "Report Type",
            name: "report_type",
            display_component(entity: SupplierInfo) {
                return config.get_form_option_label("report_type", entity.report_type) || "All";
            },
            lg: 2,
        },
        {
            label: "Report Status",
            name: "report_status",
            display_component(entity: SupplierInfo) {
                return config.get_form_option_label("report_status", entity.report_status) || "All";
            },
            lg: 2,
        },
        {
            label: "Email Recipients",
            name: "email_recipients",
            display_component(entity: SupplierInfo) {
                return <MailRecipients recipients={entity.email_recipients || {}} />;
            },
            lg: 5,
        },
    ];

    const createEmailsForm = defineInternalEmailsFormCreate(onSaveInternalEmails, onDeleteEmail);
    const createForms = [createEmailsForm];

    return <PageBase<SupplierInfo>
        title="Internal Email"
        tableColumns={tableRow}
        actionsColumnWidth={1}
        createForms={createForms as any}
        state={emailState}
    />;
};

export default InternalEmailsPage;
